import { useState } from 'react';
import { Link } from 'react-router-dom';
// ADR-071 D7 / Structure Guide v6 §9 — staff-owned navbar fork. The chip is no
// longer imported from @portal-shared/navbar; it is the staff copy (see
// ./navbar/README.md). portal-subscriber still consumes @portal-shared.
import { AiProvidersChip } from './navbar/AiProvidersChip';
import { useStaffMe } from '../hooks/useStaffMe';
import { CogDropdown } from './CogDropdown';
import { AlertIndicator } from './AlertIndicator';
import { adminAiProvidersSource } from '../lib/ai-providers-source';
import { ZoneIndicator } from './ZoneContext';
import { WorkingZoneSwitcher, ZoneSessionGate } from './ZoneSelector';
import { useAuth } from '../hooks/useAuth';

/**
 * AppHeader — the canonical Nexanomics shared navbar for the staff portal
 * (Phase 5d-1, Decision 5-26).
 *
 * Ports the three-zone shape from nexa-demo's `nexanomics-nav.js` + the
 * admin patch from `nexanomics-admin-common.js` `_patchNav`:
 *
 *   Left   — Nexanomics logo (links back to /)
 *   Centre — "NEXA Admin · PLATFORM" indicator pill (static, non-interactive
 *            — the admin-portal _patchNav swaps the subscriber-portal domain
 *            pill for this fixed indicator)
 *   Right  — staff identity (staffId · role) + Cog dropdown
 *
 * Sign Out moved from a standalone button in this header into the Cog
 * dropdown — matches the canonical shared-nav structure where every
 * persistent action lives in one menu.
 *
 * BOW-finesse B6 (30 Jun 2026) — the legacy Phase-3 AdminTabStrip that used to
 * render below this header was deleted; the COG dropdown is now the sole admin
 * navigation surface.
 *
 * ADR-123 Decision 1 — the zone indicator. A second, slim row of the navigation bar, pinned
 * beneath the first, naming the zone(s) the page is showing or will write to. It is ALWAYS
 * mounted — never conditional on how many zones the bundle knows — because an absent zone
 * indicator is indistinguishable from a hidden or broken one. It is a row of its own rather
 * than squeezed into the centre slot because "ap-southeast-2 · Australia 7 · us-east-1 · United
 * States 1" is too long to share a 52px bar with the logo and the identity without colliding.
 *
 * ADR-124 A2/A3 — the same row carries the session's working zone and its "Switch zone…" control
 * (a confirmed switch, never a silent one), and the header mounts the sign-in statement of the
 * zone, so it is present on every authenticated route by the same construction.
 */
export function AppHeader() {
  const me = useStaffMe();
  const identity = me.status === 'loaded' ? me.me : null;
  const { cognitoSub } = useAuth();
  // ADR-124 A3 → A2: the sign-in modal's "Switch zone…" opens the navigation bar's switch dialog.
  const [switchRequest, setSwitchRequest] = useState(0);

  return (
    <>
    <header className="portal-navbar" role="banner">
      <div className="portal-navbar-left">
        <Link to="/" className="portal-navbar-logo" aria-label="Nexanomics home">
          {/* BOW-finesse B5 — the ops (nexa-admin) portal uses BLUE branding
              (matches the blue admin favicon); approved recoloured mark. */}
          <img src="/nexa-logo-admin-blue.png" alt="Nexanomics" />
        </Link>
      </div>
      <div className="portal-navbar-centre">
        <div className="portal-navbar-indicator">
          <span className="portal-navbar-indicator-dot" aria-hidden="true" />
          NEXA Admin
          <span className="portal-navbar-indicator-badge">PLATFORM</span>
        </div>
      </div>
      <div className="portal-navbar-right">
        {/* Multi-provider AI health chip (30 May 2026). Replaced the
            single Anthropic chip with a dropdown listing Anthropic +
            OpenAI + xAI. Compact when closed (three coloured dots +
            "AI · 3" label); click opens the dropdown with per-provider
            latency + favicon. */}
        <AiProvidersChip source={adminAiProvidersSource} />
        {/* ADR-071 A1 — critical-only alert indicator (folds in VendorAlertBell,
            D7.4). Renders nothing when there are no active criticals. */}
        {identity && <AlertIndicator />}
        {identity && (
          <div className="portal-navbar-identity" title={[identity.name, identity.staffId, identity.role].filter(Boolean).join(' · ')}>
            {identity.name && (
              <>
                <span className="portal-navbar-name">{identity.name}</span>
                <span aria-hidden="true"> · </span>
              </>
            )}
            <code>{identity.staffId}</code>
            <span aria-hidden="true"> · </span>
            <span className="portal-navbar-role">{identity.role}</span>
          </div>
        )}
        {identity && <CogDropdown />}
      </div>
    </header>
    <div className="zone-indicator-bar">
      <ZoneIndicator />
      {/* ADR-124 A2 — the session's working zone and the console-wide switch, always rendered. */}
      <WorkingZoneSwitcher openRequest={switchRequest} />
    </div>
    {/* ADR-124 A3 — the zone is stated at every sign-in, before the console is used. Waits for
        /admin/me (loaded or failed) so the zone stated is the one the session will use. */}
    <ZoneSessionGate
      sub={cognitoSub}
      ready={me.status !== 'loading'}
      onSwitch={() => setSwitchRequest((n) => n + 1)}
    />
    </>
  );
}
